/**
 * usePermissions hook
 * Fetch and cache user permissions for feature access control
 */
import { useState, useEffect, useCallback } from 'react'
import { useAuthStore } from '../store/authStore'
import permissionService from '../services/permissionService'

// Cache permissions in memory to avoid re-fetching on every render
let cachedPermissions = null
let cacheTimestamp = 0
const CACHE_TTL = 5 * 60 * 1000 // 5 minutes

/**
 * Hook to get user permissions
 * @returns {Object} { permissions, hasAccess, isLoading, refetch }
 */
export function usePermissions() {
  const { user, isAuthenticated } = useAuthStore()
  const [permissions, setPermissions] = useState(cachedPermissions)
  const [isLoading, setIsLoading] = useState(!cachedPermissions)

  // Fetch permissions from API
  const fetchPermissions = useCallback(async () => {
    try {
      const response = await permissionService.getMyAccess()
      if (response.status === 'success' && response.data) {
        const perms = response.data
        cachedPermissions = perms
        cacheTimestamp = Date.now()
        setPermissions(perms)
      }
    } catch (error) {
      console.error('Failed to fetch permissions:', error)
      // Fallback: allow basic features for authenticated users
      const fallback = {
        role: user?.role || 'guest',
        allowed_features: isAuthenticated
          ? ['news', 'projects', 'docs', 'map', 'maps', 'profile']
          : ['news', 'projects', 'map']
      }
      setPermissions(fallback)
    } finally {
      setIsLoading(false)
    }
  }, [user?.role, isAuthenticated])

  // Fetch on mount or when auth changes
  useEffect(() => {
    const isCacheValid = cachedPermissions && (Date.now() - cacheTimestamp < CACHE_TTL)

    // Invalidate cache if user changed
    if (cachedPermissions && cachedPermissions.role !== (user?.role || 'guest')) {
      cachedPermissions = null
    }

    if (!isCacheValid) {
      setIsLoading(true)
      fetchPermissions()
    } else {
      setPermissions(cachedPermissions)
      setIsLoading(false)
    }
  }, [user?.id, user?.role, isAuthenticated, fetchPermissions])

  /**
   * Check if user has access to a feature
   * @param {string} featureKey - Feature key (e.g., 'news', 'docs', 'maps')
   * @returns {boolean}
   */
  const hasAccess = useCallback((featureKey) => {
    if (!permissions) return false

    // Admin always has access
    if (permissions.role === 'admin') return true

    // Check allowed_features array
    return permissions.allowed_features?.includes(featureKey) ?? false
  }, [permissions])

  /**
   * Force refetch permissions (e.g., after permission update)
   */
  const refetch = useCallback(() => {
    cachedPermissions = null
    cacheTimestamp = 0
    setIsLoading(true)
    fetchPermissions()
  }, [fetchPermissions])

  return {
    permissions,
    hasAccess,
    isLoading,
    refetch,
    role: permissions?.role || (user?.role || 'guest'),
    allowedFeatures: permissions?.allowed_features || []
  }
}

/**
 * Clear permission cache (call on logout)
 */
export function clearPermissionCache() {
  cachedPermissions = null
  cacheTimestamp = 0
}

export default usePermissions
